import React, { Component } from 'react';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import { isEqual } from 'lodash-es';
import { TIME_FORMAT } from 'app-constants';

import AntTimePicker, { TimePickerProps as AntTimePickerProps } from 'antd/lib/time-picker';
import { ScrollCustomStyles } from 'components/ScrollCustomStyles/ScrollCustomStyles';
import { ThemeContext } from 'app-themes';

import styles from './TimePicker.module.scss';

interface TimePickerProps extends AntTimePickerProps {}

interface TimePickerState {
	value?: Moment;
	timePickerIsOpened?: boolean;
}

class TimePicker extends Component<TimePickerProps, TimePickerState> {
	static contextType = ThemeContext;
	elem: AntTimePicker | null = null;
	state = {
		value: this.props.value,
		timePickerIsOpened: false,
	} as TimePickerState;

	componentDidUpdate(prevProps: TimePickerProps) {
		if (!isEqual(prevProps.value, this.props.value)) {
			this.setState({
				value: this.props.value,
			});
		}
	}

	onOpenChange = (isOpen: boolean) => {
		this.setState({
			timePickerIsOpened: isOpen,
		});
	};

	static getDerivedStateFromProps(props: TimePickerProps, state: TimePickerState) {
		if (state.value && props.value && !state.value.isSame(props.value)) {
			return {
				value: props.value,
			};
		}
		return null;
	}

	onKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.charCode === 13) {
			// on Enter
			if (this.elem) {
				this.elem.focus();
			}
			this.setState({
				timePickerIsOpened: !this.state.timePickerIsOpened,
			});
		}
	};

	onChange = (e: React.KeyboardEvent<HTMLDivElement>) => {
		const timeInput = e.target as HTMLInputElement;
		const val = timeInput.value.replace(':', '');
		const momentVal = moment(val, TIME_FORMAT);

		if (val.length === 4 && momentVal.isValid()) {
			this.setState({
				value: momentVal,
			});

			if (this.props.onChange) {
				this.props.onChange(momentVal, momentVal.format(TIME_FORMAT));
			}
		}
	};

	onChangeViaClick = (timeMoment: Moment, timeString: string) => {
		this.setState({
			value: timeMoment,
		});
		if (this.props.onChange) {
			this.props.onChange(timeMoment, timeString);
		}
	};

	onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.which === 9) {
			// on Tab, it is possible to handle only on keyDown event
			this.setState({
				timePickerIsOpened: false,
			});
		}
	};

	render() {
		const { className, popupClassName, open, ...restProps } = this.props;
		const theme = this.context;

		return (
			<ScrollCustomStyles>
				<div
					onKeyPress={this.onKeyPress}
					onKeyDown={this.onKeyDown}
					onChange={this.onChange}
				>
					<AntTimePicker
						{...restProps}
						className={classNames(styles.timePicker, className, {
							[styles[`timePicker-${theme}`]]: theme !== '',
							[styles[`timePickerOpened`]]: this.state.timePickerIsOpened,
						})}
						popupClassName={classNames(styles.popupTimePicker, popupClassName, {
							[styles[`popupTimePicker-${theme}`]]: theme !== '',
						})}
						suffixIcon={React.createElement(
							'i',
							{ className: styles.timePickerIcon },
							''
						)}
						ref={elem => (this.elem = elem)}
						open={this.state.timePickerIsOpened}
						value={this.state.value}
						onChange={this.onChangeViaClick}
						onOpenChange={this.onOpenChange}
					/>
				</div>
			</ScrollCustomStyles>
		);
	}
}

export default TimePicker;
